<template>
  <ModuleContainer>
    <div v-if="legendsVisible" ref="legendsContainer" class="px-2 py-1"
      style="position: absolute; bottom: 5px; right: 5px; width: 14.375em; min-height: 4em; background-color: white; border: 0.0625em solid rgba(0, 0, 0, 0.2); border-radius: 0.1875em">
      <h4>
        Legends
      </h4>
      <div v-for="(item, index) in legendItems" :key="index" style="display: flex;">
        <div v-if="
          item.symbolizers[0].Polygon &&
          item.symbolizers[0].Polygon['graphic-fill']
        ">
          <img style="width: 20px;" :src="item.symbolizers[0].Polygon['graphic-fill'].url" />
        </div>
        <div v-else>
          <svg width="20" height="20">
            <polygon v-if="item.symbolizers[0].Polygon" points="0,0 20,0 20,20 0,20"
              v-bind="item.symbolizers[0].Polygon"></polygon>
            <line v-else x1="20" y1="0" x2="0" y2="20" v-bind="item.symbolizers[0].Line"></line>
          </svg>
        </div>
        <div class="ml-2">
          {{ item.name ? item.name : item.title }}
        </div>
      </div>
    </div>

    <slot />
  </ModuleContainer>
</template>

<script>
import L from "@/plugins/leaflet";
import ModuleMixin from "@/components/Map/ModuleMixin";
import { ButtonGroupControl } from "@/components/Map/leaflet/ButtonGroupControl";
import { CanvasBuilder } from "@/utils/Canvas";
import { downloadImage } from "@/utils/Image";

export default {
  mixins: [ModuleMixin],

  props: {
    legendsControlPosition: {
      type: String,
      default: "bottomright",
      validator(value) {
        return (
          ["topleft", "topright", "bottomleft", "bottomright"].indexOf(
            value
          ) !== -1
        );
      }
    },

    customOverlayLayers: {
      type: Array,
      default: undefined
    }
  },

  data() {
    return {
      legendsVisible: false,
      legendItems: []
    };
  },

  computed: {
    legendUrlList() {
      return window.config.LEGENDS;
    }
  },

  async mounted() {
    for (const legenditem of this.legendUrlList) {
      const response = await fetch(legenditem);
      const data = await response.json();
      const legends = data.Legend.flatMap(item => item.rules);
      this.legendItems.push(...legends);
      this.legendItems = this.legendItems.sort((a, b) =>
        (a.title || a.name).localeCompare(b.title || b.name)
      );
    }
  },

  provide() {
    return {
      printLegends: this.printLegends
    };
  },

  methods: {
    // Mapping: tên layer địa danh → tên layer label tương ứng
    getLabelMap() {
      return {
        "Aalo": "Aalo Label",
        "Bomdilla": "Bomdila Label",
        "Chowkham": "Chowkham Label",
        "Dirang": "Dirang Label",
        "Longding": "Longding Label",
        "Mechuka": "Mechuka Label",
        "Namsai": "Namsai Label",
        "Pasighat": "Pasighat Label",
        "Roing": "Roing Label",
        "Tawang": "Tawang Label",
      };
    },

    async onInit() {
      this.map.createPane("labelsPane");
      this.map.getPane("labelsPane").style.zIndex = 650;

      const baseLayers = this.createLayers(window.config.MAP.BASEMAPS);
      const allOverlays = this.createLayers(window.config.MAP.OVERLAYS);

      this.bindLabelSync(allOverlays);

      const labelNames = Object.values(this.getLabelMap());
      const visibleOverlays = Object.fromEntries(
        Object.entries(allOverlays).filter(([name]) => !labelNames.includes(name))
      );

      this.addControl(
        L.control.layers(baseLayers, visibleOverlays)
      );

      this.addControl(
        ButtonGroupControl.create([
          {
            title: "Legends",
            icon: "legend_toggle",
            onClick: () => {
              this.legendsVisible = !this.legendsVisible;
            }
          }
        ]),
        {
          position: this.legendsControlPosition
        }
      );
    },

    bindLabelSync(allOverlays) {
      const labelMap = this.getLabelMap();

      for (const [name, layer] of Object.entries(allOverlays)) {
        const labelName = labelMap[name];
        if (!labelName) continue;

        const labelLayer = allOverlays[labelName];
        if (!labelLayer) continue;

        
        layer.on("add", () => {
          if (!this.map.hasLayer(labelLayer)) {
            labelLayer.addTo(this.map);
          }
        });

       
        layer.on("remove", () => {
          if (this.map.hasLayer(labelLayer)) {
            this.map.removeLayer(labelLayer);
          }
        });
      }
    },

    createLayers(layerConfigs) {
      const result = {};

      for (const layerConfig of layerConfigs) {
        result[layerConfig.name] = this.createLayer(layerConfig);
        if (layerConfig.visible) result[layerConfig.name].addTo(this.map);
      }

      return result;
    },

    createLayer(layerConfig) {
      const labelNames = Object.values(this.getLabelMap());
      const isLabel = labelNames.includes(layerConfig.name);

      if (layerConfig.type === "tile") {
        return L.tileLayer(layerConfig.url, {
          maxNativeZoom: layerConfig.maxZoom,
          maxZoom: window.config.MAP.MAX_ZOOM,
          bounds: layerConfig.bounds
        });
      } else if (layerConfig.type === "wms") {
        return L.tileLayer.wms(layerConfig.url, {
          layers: layerConfig.layers,
          format: layerConfig.format || "image/png",
          transparent:
            layerConfig.transparent == null ? true : layerConfig.transparent,
          maxNativeZoom: layerConfig.maxZoom,
          minZoom: layerConfig.minZoom,
          maxZoom: window.config.MAP.MAX_ZOOM,
          bounds: layerConfig.bounds,
          tileSize: 256,
          pane: isLabel ? "labelsPane" : "overlayPane",
        });
      }
    },

    async printLegends() {
      const legends = this.legendItems;

      if (legends.length === 0) {
        return document.createElement("canvas");
      }

      const rect = this.$refs.legendsContainer.getBoundingClientRect();

      const horizontalPadding = 8;
      const verticalPadding = 4;

      const builder = CanvasBuilder.create(rect.width, rect.height)
        .drawRect(0, 0, rect.width, rect.height)
        .drawText(
          "Legends",
          { x: horizontalPadding, y: verticalPadding },
          {
            font: "bold 14px serif",
            textAlign: "start",
            textBaseline: "middle"
          }
        );

      const titleHeight = 15;
      const bottomMargin = 10;

      let currentY = titleHeight + bottomMargin;

      for (let i = 0, len = legends.length; i < len; i += 1) {
        const img = await downloadImage(legends[i].icon, {
          type: "image"
        });

        builder.drawImage(img, {
          left: 8,
          top: currentY + 2,
          width: 24,
          height: 14
        });

        builder.drawText(
          legends[i].name,
          {
            x: horizontalPadding + img.width + 10,
            y: currentY
          },
          {
            font: "14px serif",
            textAlign: "start",
            textBaseline: "middle"
          }
        );

        currentY += img.height + 5;
      }

      return builder.toCanvas();
    },

    onDestroy() { }
  }
};
</script>